import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { checkChange } from "utils/checkChange";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import {
  convertBrazilianDateToISO,
  convertISOToBrazilianDate,
  isValidBrazilianDateTime,
  getDateValidationError,
} from "utils/dateHelpers";
import { EventProps } from "../EventNew/useEventNew";
import { FileService } from "services/fileService";
import { formatDate } from "utils/formatDate";

const initInputsValues: EventProps = {
  name: "",
  description: "",
  organizer: "",
  logo: {
    signedFileUrl: "",
  },
  address: "",
  dateComplement: "",
  dateDescription: "",
  endDate: "",
  maximumCapacity: 0,
  isPublished: false,
  mentorshipsEnabled: false,
  showTicketQrCode: true,
  type: "PUBLIC",
  mapPdf: {
    signedFileUrl: "",
  },
  ticketPurchaseUrl: "",
};

const initTouchedInputsValues = {
  name: false,
  description: false,
  organizer: false,
  logo: false,
  address: false,
  dateComplement: false,
  dateDescription: false,
  endDate: false,
  maximumCapacity: false,
  isPublished: false,
  mapType: false,
  ticketPurchaseUrl: false,
};

export const useEventDetails = (eventId: string) => {
  const { setToast } = useToast();
  const { push } = useHistory();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  const [initialInputsValues, setInitialInputsValues] =
    useState(initInputsValues);
  const [changeFooter, setChangeFooter] = useState<string[]>([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const {
    handleSubmit,
    handleChange,
    setValues: setInputsValues,
    values: inputsValues,
    touched,
    errors,
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Nome é obrigatório"),
      description: Yup.string().optional(),
      organizer: Yup.string().optional(),
      // logoUrl: Yup.string().optional(),
      address: Yup.string().required("Endereço é obrigatório"),
      dateDescription: Yup.string().required("Descrição da data é obrigatória"),
      endDate: Yup.date().optional().nullable(),
      dateComplement: Yup.string().optional(),
      maximumCapacity: Yup.number()
        .required("Capacidade máxima é obrigatória")
        .min(1, "Capacidade deve ser maior que 0"),
      isPublished: Yup.boolean().optional(),
      mentorshipsEnabled: Yup.boolean().optional(),
      showTicketQrCode: Yup.boolean().optional(),
      ticketPurchaseUrl: Yup.string().optional(),
    }),
    onSubmit: (_) => {
      sendData();
    },
  });

  /* HOOKS */
  useEffect(() => {
    fetchEventData();
    return () => {
      cancelRequest();
    };
  }, [eventId]);

  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
  }, [inputsValues, initialInputsValues]);

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues]);

  /* EVENT HANDLERS */
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event);
  };

  const customHandleImageChange = async (file: File) => {
    setImageFile(file);
  };

  const customHandlePdfChange = async (file: File) => {
    setPdfFile(file);
  };
  /* API CALLS */
  const fetchEventData = async () => {
    setIsLoading(true);
    cancelRequest();

    const query = `
      query BackofficeEvent($eventId: ID!) {
        backofficeEvent(eventId: $eventId) {
          eventId
          name
          description
          organizer
          logo {
            signedFileUrl
          }
          address
          dateDescription
          endDate
          dateComplement
          maximumCapacity
          isPublished
          mentorshipsEnabled
          showTicketQrCode
          insertDate
          updateDate
          type
          socialMedia {
            websiteUrl
            linkedinUrl
            twitterUrl
            instagramUrl
            facebookUrl
            youtubeUrl
            tiktokUrl
          }
          mapPdf {
            signedFileUrl
          }
          mapType
          ticketPurchaseUrl
        }
      }
    `;

    const variables = {
      eventId,
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao buscar evento",
        useNewApi: true,
      });

      const eventData = (response as any).data.backofficeEvent;

      if (!eventData) {
        setToast({
          type: "error",
          message: "Evento não encontrado",
          isActive: true,
          timeToRemoveToast: 3000,
        });
        push("/events-private");
        return;
      }

      const formattedEvent = {
        ...eventData,
        maximumCapacity: parseInt(eventData.maximumCapacity, 10),
      };

      setInitialInputsValues(formattedEvent);
      setInputsValues(formattedEvent);
    } catch (error: any) {
      console.error("Error fetching event:", error);
      setHasError(true);
      setToast({
        type: "error",
        message: "Falha ao carregar evento",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendData = async () => {
    setSubmitButton(true);
    setIsLoading(true);
    cancelRequest();

    const query = `
      mutation BackofficeUpdateEvent($eventId: ID!, $input: EventInput!) {
        backofficeUpdateEvent(eventId: $eventId, input: $input) {
          eventId
          name
          description
          organizer
          logo {
            signedFileUrl
          }
          address
          dateDescription
          endDate
          dateComplement
          maximumCapacity
          isPublished
          mentorshipsEnabled
          showTicketQrCode
          insertDate
          updateDate
          mapPdf {
            signedFileUrl
          }
          type
          mapType
          socialMedia {
            websiteUrl
            linkedinUrl
            twitterUrl
            instagramUrl
            facebookUrl
            youtubeUrl
            tiktokUrl
          }
          ticketPurchaseUrl
        }
      }
    `;

    const variables = {
      eventId,
      input: {
        name: inputsValues.name.trim(),
        description: inputsValues.description?.trim() || null,
        organizer: inputsValues.organizer?.trim() || null,
        address: inputsValues.address.trim(),
        dateDescription: inputsValues.dateDescription.trim(),
        endDate: inputsValues.endDate
          ? new Date(inputsValues.endDate).toISOString()
          : null,
        dateComplement: inputsValues.dateComplement?.trim() || null,
        maximumCapacity: parseInt(inputsValues.maximumCapacity.toString(), 10),
        type: inputsValues.type,
        isPublished: inputsValues.isPublished || false,
        mentorshipsEnabled: inputsValues.mentorshipsEnabled || false,
        showTicketQrCode: inputsValues.showTicketQrCode ?? true,
        socialMedia: {
          websiteUrl: inputsValues.socialMedia?.websiteUrl?.trim() || null,
          linkedinUrl: inputsValues.socialMedia?.linkedinUrl?.trim() || null,
          twitterUrl: inputsValues.socialMedia?.twitterUrl?.trim() || null,
          instagramUrl: inputsValues.socialMedia?.instagramUrl?.trim() || null,
          facebookUrl: inputsValues.socialMedia?.facebookUrl?.trim() || null,
          youtubeUrl: inputsValues.socialMedia?.youtubeUrl?.trim() || null,
          tiktokUrl: inputsValues.socialMedia?.tiktokUrl?.trim() || null,
        },
        ticketPurchaseUrl: inputsValues.ticketPurchaseUrl?.trim() || null,
      },
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao atualizar evento",
        useNewApi: true,
      });

      if (imageFile) {
        await FileService.uploadFile({
          file: imageFile,
          refCollectionId: response.data.backofficeUpdateEvent.eventId,
          refCollectionName: "events",
          refCollectionField: "logoId",
          refCollectionFieldType: "string",
        });
      }

      if (pdfFile) {
        await FileService.uploadFile({
          file: pdfFile,
          refCollectionId: response.data.backofficeUpdateEvent.eventId,
          refCollectionName: "events",
          refCollectionField: "mapPdfId",
          refCollectionFieldType: "string",
        });
      }

      setToast({
        type: "success",
        message: "Evento atualizado com sucesso.",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      // Update initial values to reflect saved state
      const updatedEvent = (response as any).data.backofficeUpdateEvent;
      const formattedEvent = {
        ...updatedEvent,
        maximumCapacity: parseInt(updatedEvent.maximumCapacity, 10),
      };

      setInitialInputsValues(formattedEvent);
      setChangeFooter([]);
    } catch (error: any) {
      console.error("Error updating event:", error);

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const errorMessage =
          error.graphQLErrors[0].message || "Erro ao atualizar evento";
        setToast({
          type: "error",
          message: errorMessage,
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } else {
        setToast({
          type: "error",
          message:
            error.message || "Falha ao atualizar evento. Tente novamente.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
    } finally {
      setSubmitButton(false);
      setIsLoading(false);
    }
  };

  return {
    handleInputChange,
    handleSelectChange,
    inputsValues,
    touched,
    errors,
    changeFooter,
    submitButton,
    handleSubmit,
    hasError,
    isLoading,
    customHandleImageChange,
    customHandlePdfChange,
  };
};
